import { useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@/lib/query";
import { Bell, Keyboard, LogOut, ShieldCheck } from "lucide-react";
import { useMemo, type ReactNode } from "react";

import { AppSidebar } from "@/components/common/app-sidebar";
import { MotionPage } from "@/components/common/motion";
import { Topbar } from "@/components/common/topbar";
import { ShortcutHelpDialog } from "@/components/admin/shortcut-help-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { useAdminUi } from "@/contexts/AdminUiContext";
import { useHotkeys } from "@/hooks/common/use-hotkeys";
import { ADMIN_NAV } from "@/lib/nav";

export function AdminShell({ children }: { children: ReactNode }) {
  const { signOut } = useAdminAuth();
  const { focusSearch, triggerRefresh, shortcutsOpen, setShortcutsOpen } = useAdminUi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();

  useHotkeys(
    useMemo(
      () => [
        { key: "/", handler: focusSearch },
        { key: "r", ctrlOrMeta: true, shift: true, allowInInputs: true, handler: triggerRefresh },
        { key: "?", shift: true, handler: () => setShortcutsOpen(true) },
      ],
      [focusSearch, triggerRefresh, setShortcutsOpen],
    ),
  );

  const handleSignOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    signOut();
    navigate("/admin/login", { replace: true });
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar
          items={ADMIN_NAV}
          homeUrl="/admin"
          groupLabel="Administration"
          footerTitle="HealNova Admin Portal"
          footerSubtitle="Internal use only."
        />
        <SidebarInset className="bg-background">
          <Topbar
            left={
              <p className="hidden font-display text-sm font-semibold text-muted-foreground sm:block">
                HealNova Admin
              </p>
            }
          >
            <span className="hidden items-center gap-1.5 rounded-full border border-primary/20 bg-primary/8 px-2.5 py-1 text-xs font-semibold text-primary sm:inline-flex">
              <ShieldCheck className="size-3.5" aria-hidden="true" />
              Administrator
              <span className="font-normal opacity-70">· Internal Portal</span>
            </span>

            <Button
              variant="ghost"
              size="icon"
              aria-label="Keyboard shortcuts"
              onClick={() => setShortcutsOpen(true)}
            >
              <Keyboard className="size-4" />
            </Button>

            <Button variant="ghost" size="icon" aria-label="Notifications" disabled>
              <Bell className="size-4" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full p-0.5 transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  aria-label="Administrator menu"
                >
                  <Avatar className="size-9 border border-border">
                    <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
                      AD
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <DropdownMenuLabel className="space-y-0.5">
                  <p className="truncate text-sm font-semibold">Administrator</p>
                  <p className="truncate text-xs font-normal text-muted-foreground">
                    Internal Portal
                  </p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setShortcutsOpen(true)}>
                  <Keyboard className="size-4" /> Keyboard shortcuts
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void handleSignOut()}>
                  <LogOut className="size-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Topbar>

          <MotionPage as="main" key={pathname} className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            {children}
          </MotionPage>
        </SidebarInset>
      </div>

      <ShortcutHelpDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </SidebarProvider>
  );
}
